import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, IconButton, TextField, ListItemText, Autocomplete, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { useTheme } from "@mui/material/styles";
// hooks
import useSend from "../../hooks/useSend";
// services
import Gupport from "../../services/gupport";
import { sortAlphabetically } from "../../services/l10n";
import { icons } from "@local/theme";
// types
import type { SyntheticEvent } from "react";
import type { ReadonlyDeep } from "type-fest";
import type { UserId, UserName } from "../../types/user";
import type { CmdAddGlogin, CmdGetNewGlogins, NewSupportUser } from "../../types/gupport";

interface _SupportUser {
	id: UserId;
	user: NewSupportUser;
	label: `${UserName} (${UserId}) [${UserId}]`;
	search: [UserId, UserId, UserName];
	primary: `${UserName} (${UserId})`;
	secondary: UserId;
	sortKey: `${UserName}#${UserId}#${UserId}`;
}
type SupportUser = ReadonlyDeep<_SupportUser>;

const DEFAULT_LEVELS = ["gw_read"] as const;

const sortFunc = (supportUserA: SupportUser, supportUserB: SupportUser): number => (sortAlphabetically(supportUserA.sortKey, supportUserB.sortKey));

const SupportUserInvite = () => {
	const send = useSend();
	const { t } = useTranslation();
	const theme = useTheme();

	const timeoutIdRef = useRef<number | undefined>(undefined);

	const [supportUsers, setSupportUsers] = useState<ReadonlyArray<SupportUser>>([]);
	const [selectedItem, setSelectedItem] = useState<SupportUser | null>(null);
	const [invitingUser, setInvitingUser] = useState(false);
	const [optionsLoading, setOptionsLoading] = useState(false);
	const [openErrorDialog, setOpenErrorDialog] = useState(false);
	const [openInviteDialog, setOpenInviteDialog] = useState(false);

	useEffect(() => (
		() => {
			window.clearTimeout(timeoutIdRef.current);
		}
	), []);

	const fetchNewGlogins = (_searchFilter: string) => {
		if (Gupport.getNewGlogins) {
			setOptionsLoading(true);

			const cmd = {
				action: "getNewGlogins",
				data: _searchFilter,
			} as const satisfies CmdGetNewGlogins;
			send(cmd, (error, msg) => {
				if (!error && msg?.payload.status === "ok") {
					const newSupportedUsers = msg.payload.data.map((supportUser) => ({
						id: supportUser.supUserId,
						user: supportUser,
						label: `${supportUser.displayName} (${supportUser.userName}) [${supportUser.supUserId}]`,
						search: [supportUser.supUserId, supportUser.userName, supportUser.displayName],
						primary: `${supportUser.displayName} (${supportUser.userName})`,
						secondary: supportUser.supUserId,
						sortKey: `${supportUser.displayName}#${supportUser.userName}#${supportUser.supUserId}`,
					} as const)).toSorted(sortFunc);
					setOptionsLoading(false);
					setSupportUsers(newSupportedUsers);
				} else {
					setOpenInviteDialog(false);
					setSupportUsers([]);
				}
			});
		}
	};

	const handleInputChange = (event: SyntheticEvent, value: string) => {
		if (value.length >= 3) {
			window.clearTimeout(timeoutIdRef.current);
			timeoutIdRef.current = window.setTimeout(() => {
				fetchNewGlogins(value);
			}, 500);
		}
	};

	const showInviteDialog = () => {
		setOpenInviteDialog(true);
		setSupportUsers([]);
		setSelectedItem(null);
	};

	const handleDialogClose = () => {
		setOpenInviteDialog(false);
		console.info(selectedItem);
		setSelectedItem(null);
		setOpenErrorDialog(false);
	};

	const handleInviteUserClick = () => {
		setInvitingUser(true);
		const cmd = {
			action: "addGlogin",
			...selectedItem?.user, // TODO
			level: DEFAULT_LEVELS,
		} as const satisfies CmdAddGlogin;
		send(cmd, (error, msg) => {
			if (!error && msg?.payload.status === "ok") {
				setOpenInviteDialog(false);
				setSelectedItem(null);
				setOpenErrorDialog(false);
			} else {
				setOpenInviteDialog(false);
				setSelectedItem(null);
				setOpenErrorDialog(true);
			}
			setInvitingUser(false);
		});
	};

	return (
		<div style={{ display: "inline-block" }}>
			<IconButton onClick={showInviteDialog}>
				<icons.PersonAdd style={{ display: "block" }} htmlColor={theme.vars.palette.primary.main} />
			</IconButton>
			<Dialog
				fullWidth={true}
				maxWidth="md"
				open={openInviteDialog}
				onClose={handleDialogClose}
			>
				<DialogTitle>{t("supportUser.inviteDialogTitle")}</DialogTitle>
				<DialogContent>
					<DialogContentText>{t("supportUser.usernameMsg")}</DialogContentText>
					<Autocomplete
						id="autocomplete_user_invite"
						fullWidth={true}
						color="inherit"
						loading={optionsLoading}
						options={supportUsers}
						filterOptions={(x) => (x)}
						isOptionEqualToValue={(option, value) => (
							Boolean(option.label && value.label) && option.label === value.label
						)}
						value={selectedItem}
						onChange={(event, value) => (setSelectedItem(value))}
						onInputChange={handleInputChange}
						renderInput={(params) => (
							<TextField autoFocus={true} {...params} placeholder="Search New-User" />
						)}
						renderOption={({ key, ...optionProps }, option/*, state, ownerState*/) => (
							<li key={key} {...optionProps}><ListItemText primary={option.primary} secondary={option.secondary} /></li>
						)}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						loading={invitingUser}
						disabled={selectedItem === null}
						onClick={handleInviteUserClick}
					>
						{t("supportUser.addUser")}
					</Button>
					<Button color="inherit" onClick={handleDialogClose}>{t("dialog.cancel")}</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				fullWidth={true}
				open={openErrorDialog}
				onClose={handleDialogClose}
			>
				<DialogTitle>{t("supportUser.inviteDialogTitle")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{selectedItem?.user.supUserId ?? ""} {t("supportUser.addUserError")}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleDialogClose}>{t("dialog.ok")}</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default SupportUserInvite;
